import { Star } from "lucide-react";
import { GOOGLE_REVIEWS } from "../lib/site-data";
import "./GoogleReviews.css";

function Stars({ rating, size = 14 }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => {
        const filled = i < full || (i === full && half);
        return (
          <Star
            key={i}
            size={size}
            className={filled ? "fill-[#FBBC04] text-[#FBBC04]" : "text-gray-300"}
          />
        );
      })}
    </div>
  );
}

function GoogleLogo() {
  return (
    <div className="flex items-center gap-2" aria-label="Google">
      <svg viewBox="0 0 48 48" className="h-6 w-6" aria-hidden="true">
        <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
        <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z" />
        <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
        <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571c.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
      </svg>
      <span
        className="text-xl font-medium leading-none"
        style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.01em" }}
      >
        <span style={{ color: "#4285F4" }}>G</span>
        <span style={{ color: "#EA4335" }}>o</span>
        <span style={{ color: "#FBBC04" }}>o</span>
        <span style={{ color: "#4285F4" }}>g</span>
        <span style={{ color: "#34A853" }}>l</span>
        <span style={{ color: "#EA4335" }}>e</span>
      </span>
    </div>
  );
}

function ReviewCard({ r, idx }) {
  return (
    <article
      data-testid={`google-review-${idx}`}
      className="shrink-0 w-[320px] md:w-[360px] rounded-2xl border border-border p-5 bg-background flex flex-col"
    >
      <div className="flex items-center gap-3 mb-3">
        <span
          className="h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold shrink-0"
          style={{ backgroundColor: r.color }}
        >
          {r.initial}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight truncate">
            {r.name}
          </p>
          <p className="text-xs text-muted-foreground">{r.timeAgo}</p>
        </div>
        <span className="ml-auto" aria-label="From Google">
          <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z" />
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571c.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
          </svg>
        </span>
      </div>
      <Stars rating={r.rating} />
      <p className="mt-3 text-sm text-foreground/80 leading-relaxed">
        {r.text}
      </p>
    </article>
  );
}

export default function GoogleReviews() {
  const { rating, reviews } = GOOGLE_REVIEWS;
  const half = Math.ceil(reviews.length / 2);
  const rowA = reviews.slice(0, half);
  const rowB = reviews.slice(half).concat(reviews.slice(0, 1)); // ensure row B has enough items
  // duplicate each row for seamless loop
  const trackA = [...rowA, ...rowA];
  const trackB = [...rowB, ...rowB];

  return (
    <section
      id="reviews"
      data-testid="google-reviews-section"
      className="py-24 md:py-32 bg-muted/40 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 mb-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="label-eyebrow">From Google</p>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-tight max-w-2xl">
              Loved by job seekers across the UK
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <GoogleLogo />
            <div className="h-10 w-px bg-border" />
            <div className="flex items-center gap-3">
              <span className="text-3xl font-serif font-bold leading-none">
                {rating.toFixed(1)}
              </span>
              <Stars rating={rating} size={18} />
            </div>
          </div>
        </div>
      </div>

      {/* Row A — left to right */}
      <div className="reviews-marquee mb-4">
        <div className="reviews-marquee__track">
          {trackA.map((r, i) => (
            <ReviewCard key={`a-${i}`} r={r} idx={`a-${i}`} />
          ))}
        </div>
      </div>

      {/* Row B — right to left */}
      <div className="reviews-marquee">
        <div className="reviews-marquee__track reviews-marquee__track--reverse">
          {trackB.map((r, i) => (
            <ReviewCard key={`b-${i}`} r={r} idx={`b-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
}
